import * as Sentry from "@sentry/tanstackstart-react"
import { useRouter } from "@tanstack/react-router"
import { useSession } from "@/lib/auth-client"
import { ErrorComponent } from "./ErrorComponent"

type SentryErrorBoundaryProps = {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>
  showDialog?: boolean
}

export function SentryErrorBoundary({
  children,
  fallback: customFallback,
  showDialog = true,
}: SentryErrorBoundaryProps) {
  const router = useRouter()
  const { data: session } = useSession()

  return (
    <Sentry.ErrorBoundary
      // eslint-disable-next-line @typescript-eslint/unbound-method
      fallback={({ error, resetError, eventId }) => {
        const errorObj = error instanceof Error ? error : new Error(String(error))
        if (customFallback) {
          const CustomFallback = customFallback
          return <CustomFallback error={errorObj} resetError={resetError} />
        }
        return <ErrorComponent error={errorObj} reset={resetError} eventId={eventId} />
      }}
      showDialog={showDialog}
      beforeCapture={(scope, error) => {
        // Add additional context to Sentry error reports
        scope.setTag("component", "react-error-boundary")
        scope.setContext("router", {
          currentRoute: router.state.location.pathname,
          currentSearch: router.state.location.search,
        })

        if (session?.user) {
          scope.setUser({
            id: session.user.id,
            email: session.user.email,
            username: session.user.name,
          })
        }

        scope.setLevel("error")

        // Add component stack trace if available
        if (error instanceof Error && error.stack) {
          scope.setContext("errorStack", {
            stack: error.stack,
          })
        }
      }}
      onError={(error, errorInfo) => {
        // Log to console in development
        if (import.meta.env.DEV) {
          console.error("ErrorBoundary caught an error:", error)
          console.error("Error info:", errorInfo)
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
